import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client/react';

import { useSoftwareEscrow } from 'src/context/software-escrow';
import { EscrowStatusEnum } from 'src/graphql/schema';
import { ClientChangesRequestedDocument } from 'src/graphql/actions/suggestChanges/clientChangesRequested.query';
import { cn } from 'src/lib/utils';
import { OtherEscrowStatusEnum } from 'src/shared/constants';
import { Button } from '@/components/ui/button';
import { EscrowAlertBadge } from 'src/components/Escrow/AlertBadge';

import { getPendingSuggestionPriority, getActionBannerStatus } from '../lib';

import ReviewRequestTermination from './ReviewRequestTermination';
import RemoveAction from './RemoveAction';

const suggestionsUrlMap = {
  agreement: 'settings/agreement',
  deposits: 'deposits',
} as const;

const GoToSuggestionsButton = () => {
  const {
    softwareEscrow: { id: softwareEscrowId, currentCompanyRole },
  } = useSoftwareEscrow();
  const { data: changesRequested } = useQuery(ClientChangesRequestedDocument);
  const navigate = useNavigate();
  const priority = useMemo(
    () => getPendingSuggestionPriority(currentCompanyRole!, changesRequested),
    [changesRequested],
  );

  if (priority === 'none') return null;

  const onGoToSuggestions = () => navigate(`/software-escrows/${softwareEscrowId}/${suggestionsUrlMap[priority]}`);

  return (
    <Button onClick={onGoToSuggestions} variant="secondary" size="sm">
      Go to requests
      <ArrowRight />
    </Button>
  );
};

const actionBannerMap = {
  [OtherEscrowStatusEnum.ChangesRequestedSuggester]: {
    title: 'Change requests submitted',
    description: 'The change requests have been submitted. We will notify you of the outcome as soon as possible.',
    color: 'text-orange-800',
    bgColor: 'bg-orange-50',
    borderColor: '',
    icon: <EscrowAlertBadge variant="warning" className="size-5" iconClassName="size-4.5" />,
    action: null,
  },
  [OtherEscrowStatusEnum.ChangesRequestedReceiver]: {
    title: 'Change requests submitted',
    description: 'The change requests have been submitted. We will notify you of the outcome as soon as possible.',
    color: 'text-orange-800',
    bgColor: 'bg-orange-50',
    borderColor: '',
    icon: <EscrowAlertBadge variant="warning" className="size-5" iconClassName="size-4.5" />,
    action: null,
  },
  [OtherEscrowStatusEnum.ChangesRequestedInReview]: {
    title: 'You have change requests to review',
    description: 'Your counterpart has requested changes. Please review them as soon as possible.',
    color: 'text-orange-800',
    bgColor: 'bg-orange-50',
    borderColor: '',
    icon: <EscrowAlertBadge variant="warning" className="size-5" iconClassName="size-4.5" />,
    action: GoToSuggestionsButton,
  },
  [OtherEscrowStatusEnum.TerminationRequestedReceiver]: {
    title: 'Termination requested',
    description: 'A request to terminate the escrow has been submitted. Please review the request as soon as possible.',
    color: 'text-rose-800',
    bgColor: 'bg-rose-50',
    borderColor: '',
    icon: <EscrowAlertBadge variant="alert" className="size-5" iconClassName="size-4.5" />,
    action: ReviewRequestTermination,
  },
  [OtherEscrowStatusEnum.TerminationRequestedReceiverViewer]: {
    title: 'Termination requested',
    description: 'A request to terminate the escrow has been submitted.',
    color: 'text-rose-800',
    bgColor: 'bg-rose-50',
    borderColor: '',
    icon: <EscrowAlertBadge variant="alert" className="size-5" iconClassName="size-4.5" />,
    action: null,
  },
  [OtherEscrowStatusEnum.TerminationRequestedSuggester]: {
    title: 'Termination requested',
    description:
      'Your termination request has been submitted for approval. We will notify you of the outcome as soon as possible.',
    color: 'text-rose-800',
    bgColor: 'bg-rose-50',
    borderColor: '',
    icon: <EscrowAlertBadge variant="alert" className="size-5" iconClassName="size-4.5" />,
    action: null,
  },
  [EscrowStatusEnum.PendingTermination]: {
    title: 'Termination scheduled',
    description:
      'The escrow termination request has been accepted and is now pending. It is scheduled to be terminated on ',
    color: 'text-rose-800',
    bgColor: 'bg-rose-50',
    borderColor: '',
    icon: <EscrowAlertBadge variant="alert" className="size-5" iconClassName="size-4.5" />,
    action: null,
  },
  [EscrowStatusEnum.Terminated]: {
    title: 'Escrow terminated',
    description: 'This escrow has been terminated, and is no longer in effect.',
    color: 'text-rose-800',
    bgColor: 'bg-rose-50',
    borderColor: '',
    icon: <EscrowAlertBadge variant="alert" className="size-5" iconClassName="size-4.5" />,
    action: null,
  },
  [OtherEscrowStatusEnum.Rejected]: {
    title: 'Escrow invitation declined',
    description:
      'The other party rejected the invitation to collaborate on this escrow. Please delete this escrow and start a new one if needed.',
    color: 'text-slate-800',
    bgColor: 'bg-accent',
    borderColor: 'border border-border',
    icon: null,
    action: RemoveAction,
  },
};

const ActionBanner = () => {
  const {
    softwareEscrow,
    softwareEscrow: { escrowTermination, escrowInvitation },
  } = useSoftwareEscrow();
  const { data: changesRequested } = useQuery(ClientChangesRequestedDocument);

  const status = getActionBannerStatus({
    escrowStatus: softwareEscrow.status,
    currentCompanyRole: softwareEscrow.currentCompanyRole,
    changesRequested,
    escrowTermination,
    escrowInvitationStatus: escrowInvitation?.status,
  });

  const bannerConfig = actionBannerMap[status as keyof typeof actionBannerMap];

  if (!bannerConfig) return null;

  const { title, color, bgColor, icon, action: Action, borderColor } = bannerConfig;
  let description = bannerConfig.description;

  if (softwareEscrow.status === EscrowStatusEnum.PendingTermination) {
    description += ` ${dayjs(escrowTermination?.terminateAt).format('DD MMMM YYYY')}.`;
  }

  return (
    <div className="container mx-auto mb-6">
      <div
        className={cn(
          'flex flex-col justify-between gap-4 md:flex-row md:items-center md:gap-2 rounded-md py-4 px-5',
          bgColor,
          borderColor,
        )}
      >
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            {icon}
            <h5 className={cn('text-sm', color)}>{title}</h5>
          </div>
          <p className={cn('text-sm opacity-70', color)}>{description}</p>
        </div>
        {Action && <Action />}
      </div>
    </div>
  );
};

export default ActionBanner;
